import { useState, useMemo } from 'react';
import { Search, Check, AlertTriangle, Circle, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { CaseIntegrityPanel } from './CaseIntegrityPanel';

interface CaseEntry {
  id: string;
  patientName: string;
  patientId: string;
  dateOfService: string;
  surgeon: string;
  payer: string;
  tripleMatch: {
    authorization: 'match' | 'gap' | 'pending';
    documentation: 'match' | 'gap' | 'pending';
    surgeonConfirmation: 'match' | 'gap' | 'pending';
  };
  procedure: string;
  cptCode: string;
}

const mockCases: CaseEntry[] = [
  {
    id: 'CASE-4821',
    patientName: 'Margaret Thompson',
    patientId: 'PT-90214',
    dateOfService: '2026-02-10',
    surgeon: 'Dr. John Larkin',
    payer: 'BCBS',
    tripleMatch: { authorization: 'match', documentation: 'match', surgeonConfirmation: 'match' },
    procedure: 'Total Knee Arthroplasty',
    cptCode: '27447',
  },
  {
    id: 'CASE-4822',
    patientName: 'Robert Chen',
    patientId: 'PT-90215',
    dateOfService: '2026-02-10',
    surgeon: 'Dr. Sarah Mitchell',
    payer: 'Medicare',
    tripleMatch: { authorization: 'match', documentation: 'match', surgeonConfirmation: 'pending' },
    procedure: 'Rotator Cuff Repair',
    cptCode: '29827',
  },
  {
    id: 'CASE-4823',
    patientName: 'Diana Vasquez',
    patientId: 'PT-90216',
    dateOfService: '2026-02-09',
    surgeon: 'Dr. John Larkin',
    payer: 'Aetna',
    tripleMatch: { authorization: 'match', documentation: 'gap', surgeonConfirmation: 'pending' },
    procedure: 'ACL Reconstruction',
    cptCode: '29888',
  },
  {
    id: 'CASE-4824',
    patientName: 'James Whitfield',
    patientId: 'PT-90217',
    dateOfService: '2026-02-09',
    surgeon: 'Dr. Mark Evans',
    payer: 'Workers Comp (BWC)',
    tripleMatch: { authorization: 'match', documentation: 'match', surgeonConfirmation: 'match' },
    procedure: 'Meniscectomy',
    cptCode: '29881',
  },
  {
    id: 'CASE-4825',
    patientName: 'Patricia Nguyen',
    patientId: 'PT-90218',
    dateOfService: '2026-02-08',
    surgeon: 'Dr. Sarah Mitchell',
    payer: 'UnitedHealthcare',
    tripleMatch: { authorization: 'match', documentation: 'match', surgeonConfirmation: 'pending' },
    procedure: 'Shoulder Decompression',
    cptCode: '29826',
  },
  {
    id: 'CASE-4826',
    patientName: 'William Torres',
    patientId: 'PT-90219',
    dateOfService: '2026-02-08',
    surgeon: 'Dr. John Larkin',
    payer: 'BCBS',
    tripleMatch: { authorization: 'match', documentation: 'gap', surgeonConfirmation: 'pending' },
    procedure: 'Total Hip Arthroplasty',
    cptCode: '27130',
  },
];

function TripleMatchIcon({ status }: { status: 'match' | 'gap' | 'pending' }) {
  if (status === 'match') {
    return (
      <div className="w-6 h-6 rounded-full bg-success flex items-center justify-center" title="Matched">
        <Check className="w-3.5 h-3.5 text-success-foreground" />
      </div>
    );
  }
  if (status === 'gap') {
    return (
      <div className="w-6 h-6 rounded-full bg-warning flex items-center justify-center" title="Gap Detected">
        <AlertTriangle className="w-3.5 h-3.5 text-warning-foreground" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-full border-2 border-muted-foreground/40 flex items-center justify-center" title="Pending">
      <Circle className="w-3 h-3 text-muted-foreground/40" />
    </div>
  );
}

export function LiveCaseFeed() {
  const [search, setSearch] = useState('');
  const [discrepancyOnly, setDiscrepancyOnly] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseEntry | null>(null);

  const filtered = useMemo(() => {
    let cases = mockCases;
    if (search) {
      const q = search.toLowerCase();
      cases = cases.filter(
        (c) =>
          c.patientName.toLowerCase().includes(q) ||
          c.patientId.toLowerCase().includes(q) ||
          c.cptCode.includes(q) ||
          c.procedure.toLowerCase().includes(q)
      );
    }
    if (discrepancyOnly) {
      cases = cases.filter(
        (c) =>
          c.tripleMatch.authorization === 'gap' ||
          c.tripleMatch.documentation === 'gap' ||
          c.tripleMatch.surgeonConfirmation === 'gap'
      );
    }
    return cases;
  }, [search, discrepancyOnly]);

  return (
    <div className="glass-card overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
        <h2 className="text-sm font-semibold tracking-tight">Live Case Feed — Procedure Revenue Integrity</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Patient or CPT..."
              className="h-8 w-52 pl-8 pr-3 rounded-md bg-secondary/60 border border-border text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground whitespace-nowrap">Discrepancies Only</span>
            <Switch
              checked={discrepancyOnly}
              onCheckedChange={setDiscrepancyOnly}
              className="scale-75"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Date of Service</th>
              <th>Surgeon</th>
              <th>Payer</th>
              <th>
                <div className="flex items-center gap-1">
                  Triple-Match Status
                  <span className="text-[9px] font-normal normal-case tracking-normal text-muted-foreground ml-1">(Auth · Doc · Surgeon)</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const hasGap =
                c.tripleMatch.authorization === 'gap' ||
                c.tripleMatch.documentation === 'gap';
              return (
                <tr
                  key={c.id}
                  onClick={() => setSelectedCase(c)}
                  className={cn(
                    'transition-colors cursor-pointer hover:bg-accent/40',
                    hasGap && 'bg-warning/5'
                  )}
                >
                  <td>
                    <div className="flex flex-col">
                      <span className="font-medium text-foreground text-sm">{c.patientName}</span>
                      <span className="text-[11px] text-muted-foreground font-mono">{c.patientId} · {c.cptCode} {c.procedure}</span>
                    </div>
                  </td>
                  <td className="text-sm font-mono text-muted-foreground">{c.dateOfService}</td>
                  <td className="text-sm text-foreground">{c.surgeon}</td>
                  <td>
                    <span className="status-badge status-badge-muted text-xs">{c.payer}</span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <TripleMatchIcon status={c.tripleMatch.authorization} />
                      <TripleMatchIcon status={c.tripleMatch.documentation} />
                      <TripleMatchIcon status={c.tripleMatch.surgeonConfirmation} />
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                  No cases match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CaseIntegrityPanel
        caseData={selectedCase}
        open={!!selectedCase}
        onOpenChange={(open) => { if (!open) setSelectedCase(null); }}
      />
    </div>
  );
}
